import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import logo from '../images/logo.png';
import { CONTACT, causes } from './siteData';
import {
  BiLogoFacebookSquare,
  BiLogoInstagramAlt,
  BiLogoWhatsapp,
  BiSolidEnvelope,
  BiSolidMap,
  BiSolidPhone,
} from 'react-icons/bi';

function homeAnchor(id) {
  return `/#${id}`;
}

function SiteHeader() {
  return (
    <header className="site-topbar" aria-label="Contact information">
      <div className="container">
        <div className="topbar-grid">
          <div className="topbar-contact">
            <span><BiSolidMap aria-hidden="true" /> {CONTACT.shortLocation}</span>
            <a href={CONTACT.emailHref}><BiSolidEnvelope aria-hidden="true" /> {CONTACT.email}</a>
            <a href={CONTACT.phoneHref}><BiSolidPhone aria-hidden="true" /> {CONTACT.phone}</a>
          </div>
          <div className="topbar-social" aria-label="Social media">
            <span>Follow us</span>
            <a href={CONTACT.facebook} target="_blank" rel="noreferrer" aria-label="Raising Smiles on Facebook">
              <BiLogoFacebookSquare aria-hidden="true" />
            </a>
            <a href={CONTACT.instagram} target="_blank" rel="noreferrer" aria-label="Raising Smiles on Instagram">
              <BiLogoInstagramAlt aria-hidden="true" />
            </a>
          </div>
        </div>
      </div>
    </header>
  );
}

function SiteNavbar() {
  const { pathname } = useLocation();
  const isHome = pathname === '/';

  const linkFor = (id) => (isHome ? `#${id}` : homeAnchor(id));

  return (
    <nav className="navbar navbar-expand-lg site-navbar sticky-top" aria-label="Primary navigation">
      <div className="container">
        <Link to="/" className="navbar-brand brand-lockup">
          <img src={logo} className="logo" alt="Raising Smiles logo" />
          <span>
            Raising Smiles
            <small>making a difference</small>
          </span>
        </Link>

        <button
          className="navbar-toggler"
          type="button"
          data-bs-toggle="collapse"
          data-bs-target="#navbarNav"
          aria-controls="navbarNav"
          aria-expanded="false"
          aria-label="Toggle navigation"
        >
          <span className="navbar-toggler-icon"></span>
        </button>

        <div className="collapse navbar-collapse" id="navbarNav">
          <ul className="navbar-nav ms-auto align-items-lg-center">
            <li className="nav-item"><a className="nav-link" href={linkFor('home')}>Home</a></li>
            <li className="nav-item"><a className="nav-link" href={linkFor('about')}>About</a></li>
            <li className="nav-item"><a className="nav-link" href={linkFor('causes')}>Causes</a></li>
            <li className="nav-item"><a className="nav-link" href={linkFor('impact')}>Our Impact</a></li>
            <li className="nav-item"><Link className="nav-link" to="/news">News</Link></li>
            <li className="nav-item"><Link className="nav-link" to="/videos">Videos</Link></li>
            <li className="nav-item"><a className="nav-link" href={linkFor('contact')}>Contact</a></li>
            <li className="nav-item">
              <a className="btn btn-donate-nav" href={CONTACT.donate} target="_blank" rel="noreferrer">
                Donate
              </a>
            </li>
          </ul>
        </div>
      </div>
    </nav>
  );
}

function SiteFooter() {
  return (
    <footer className="site-footer">
      <div className="container">
        <div className="footer-grid">
          <div className="footer-brand">
            <img src={logo} className="logo-b" alt="Raising Smiles logo" />
            <p>Community-led support for medical aid, education, orphanages, and social causes across Udupi and beyond.</p>
            <a className="btn btn-light footer-donate" href={CONTACT.donate} target="_blank" rel="noreferrer">Donate Now</a>
          </div>

          <div>
            <h5 className="site-footer-title">Navigation</h5>
            <ul className="footer-menu">
              <li><a href={homeAnchor('about')}>About Us</a></li>
              <li><a href={homeAnchor('impact')}>Our Impact</a></li>
              <li><a href={homeAnchor('volunteer')}>Volunteer</a></li>
              <li><Link to="/news">News</Link></li>
              <li><Link to="/videos">Videos</Link></li>
            </ul>
          </div>

          <div>
            <h5 className="site-footer-title">Causes</h5>
            <ul className="footer-menu">
              {causes.map((cause) => (
                <li key={cause.title}><a href={homeAnchor('causes')}>{cause.title}</a></li>
              ))}
            </ul>
          </div>

          <div>
            <h5 className="site-footer-title">Contact</h5>
            <ul className="footer-contact">
              <li><BiSolidPhone aria-hidden="true" /><a href={CONTACT.phoneHref}>{CONTACT.phone}</a></li>
              <li><BiSolidEnvelope aria-hidden="true" /><a href={CONTACT.emailHref}>{CONTACT.email}</a></li>
              <li><BiSolidMap aria-hidden="true" /><span>{CONTACT.location}</span></li>
            </ul>
            <div className="footer-social" aria-label="Social links">
              <a href={CONTACT.facebook} target="_blank" rel="noreferrer" aria-label="Facebook"><BiLogoFacebookSquare /></a>
              <a href={CONTACT.instagram} target="_blank" rel="noreferrer" aria-label="Instagram"><BiLogoInstagramAlt /></a>
            </div>
          </div>
        </div>
      </div>
      <div className="site-footer-bottom">
        <div className="container d-md-flex justify-content-between align-items-center">
          <p className="copyright-text mb-2 mb-md-0">Copyright 2026 Raising Smiles</p>
          <p className="copyright-text mb-0">Designed & Developed by <a href="https://www.sudheshholla.in/" target="_blank" rel="noreferrer">Sudhesh Holla</a></p>
        </div>
      </div>
    </footer>
  );
}

export default function SiteLayout({ children }) {
  return (
    <div className="site-shell">
      <a className="whatsapp-button" target="_blank" rel="noreferrer" href={CONTACT.whatsapp} aria-label="Chat with Raising Smiles on WhatsApp">
        <BiLogoWhatsapp aria-hidden="true" />
      </a>
      <SiteHeader />
      <SiteNavbar />
      {children}
      <SiteFooter />
    </div>
  );
}

export function PageHero({ eyebrow, title, children }) {
  return (
    <section className="page-hero">
      <div className="container">
        {eyebrow && <span className="eyebrow">{eyebrow}</span>}
        <h1>{title}</h1>
        {children && <p>{children}</p>}
      </div>
    </section>
  );
}
